import { Seo, StructuredData } from '@/components/molecules';
import { ImportPromiseBand, PageIntro, QuoteBand } from '@/components/organisms';
import { type WhatsAppRouteHandle } from '@/hooks/usePageWhatsAppMessage';
import { breadcrumbSchema, organizationSchema } from '@/utils/structuredData';

import { closing, crumbs, intro, introHighlights, REQUEST_ID, WHATSAPP_MESSAGE } from './content';
import { AdviceRequestBand, MeetingWaysBand } from './organisms';

/** Opens WhatsApp with the context of this page (§60 K/L). */
export const handle: WhatsAppRouteHandle = { whatsappMessage: WHATSAPP_MESSAGE };

/**
 * §32 — free import advice, on a page of its own. Four bands and no logic.
 *
 * The form was already on /contact and it still is. This page exists because a
 * band inside a contact page cannot be linked to from the site chrome: the
 * header's "Get Free Advice" has to land somewhere that is about the advice,
 * with its own title, its own breadcrumb trail and its own entry in the
 * sitemap — not two-thirds of the way down a page about addresses and opening
 * hours.
 *
 * The two share every part that matters. The form, the questions, the
 * submission and the four steps all come from `components/consultation` and
 * `content/consultation.ts`, so neither page can ask something the other does
 * not or promise something the other has stopped promising. What differs is
 * the arrangement around them, which is what a page is.
 *
 * What it deliberately does NOT carry is an argument for asking at all. That
 * is `/services/import-consultation` — the six inclusions, the age limit, the
 * three questions people ask — and the band below the form links to it.
 * Restating it here would put the same copy on two URLs and leave the one
 * without a form to win the search.
 *
 * The order is the order the visitor needs it in: what this is and what it
 * costs, the ask, how the answer comes back, then the other way in. The
 * intro's action points at the form's own band rather than at another page,
 * because on a phone the form is below the fold and a visitor who arrived from
 * the header has already decided.
 *
 * The page holds the only h1 and no state.
 */
export default function ImportAdvicePage() {
  return (
    <>
      <StructuredData
        data={[organizationSchema(), breadcrumbSchema([{ label: 'Home', to: '/' }, ...crumbs])]}
      />
      <Seo
        title="Free Import Advice"
        description="Ask Kabex Motors before you import. Which years Uganda still allows, whether a model is worth importing, and what it will cost on the road. Free, and not conditional on buying."
        path="/free-import-advice"
      />

      <PageIntro
        eyebrow={intro.eyebrow}
        title={intro.title}
        lede={intro.lede}
        crumbs={crumbs}
        slideshow
        highlights={introHighlights}
        action={{ label: intro.action, to: `#${REQUEST_ID}` }}
        whatsapp
      />

      <AdviceRequestBand />

      <MeetingWaysBand />

      <ImportPromiseBand />

      <QuoteBand
        title={closing.title}
        text={closing.text}
        whatsappMessage={closing.whatsappMessage}
      />
    </>
  );
}
